import {
  Box,
  HStack,
  VStack,
  Text,
  Circle,
  useBoolean,
  Skeleton,
} from '@wegroup/design-system';
import React, { useContext } from 'react';
import SingleSelectedOfferPrice from './SingleSelectedOfferPrice';
import SingleSelectedSubOffer from './SingleSelectedSubOffer';
import { IconActionDropDown } from 'wg-fe-ui';
import styled, { ThemeContext } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/redux/reducers/rootReducer';
import { setPreferredOffer } from '../../../store/redux/slices/offersSlice';
import { useQuote } from '../../../hooks/useQuoteQueries';
import { getOfferState } from '../../../services/offersService';
import { getEuroValue } from '../../../services/productFactoryService';
import { useTranslation } from 'react-i18next';

interface Props {
  Icon: React.ReactElement;
  title: string;
  riskObject: string;
  preferredOfferId: string;
  multiple?: boolean;
}

const SingleSelectedOffer: React.FC<React.PropsWithChildren<Props>> = ({
  Icon,
  title,
  multiple,
  riskObject,
  preferredOfferId,
  ...otherProps
}) => {
  const theme = useContext(ThemeContext);
  const dispatch = useDispatch();
  const selectedOffers = useSelector(
    (state: RootState) => state.offers.selectedOffers,
  );

  const [isSubMenuOpen, setIsSubMenuOpen] = useBoolean(false);

  const choosePreferredOffer = (quoteId: string) =>
    dispatch(setPreferredOffer({ riskObject, id: quoteId }));

  return (
    <VStack
      spacing={2}
      width="100%"
      data-test-id="ADVISORY-CONVO_OFFERS_SELECTED-OFFER_WRAPPER"
    >
      <HStack width="100%" spacing={2} fontSize="md" {...otherProps}>
        <Box borderRadius="6px" backgroundColor="gray.ultraLight" padding={1}>
          {Icon}
        </Box>
        <VStack align="flex-start" justify="center" flexGrow={1} minH="12">
          <Text>{title}</Text>
          <SinglePreferredOffer
            riskObject={riskObject}
            preferredOfferId={preferredOfferId}
          />
        </VStack>
        {multiple && (
          <Circle
            backgroundColor="primary.50"
            cursor="pointer"
            onClick={setIsSubMenuOpen.toggle}
            transform={isSubMenuOpen ? 'rotate(180deg)' : ''}
            transition="0.2s"
          >
            <StyledIconActionDropDown color={theme.primary[500]} />
          </Circle>
        )}
      </HStack>
      {multiple && isSubMenuOpen && (
        <VStack width="100%" spacing={3}>
          {selectedOffers[riskObject].map((id) => (
            <SingleSelectedSubOffer
              key={`selected_offer_box${id}`}
              riskObject={riskObject}
              id={id}
              onClick={() => choosePreferredOffer(id)}
              isSelected={id === preferredOfferId}
            />
          ))}
        </VStack>
      )}
    </VStack>
  );
};

interface SinglePreferredOfferProps {
  riskObject: string;
  preferredOfferId: string;
}

const SinglePreferredOffer: React.FC<
  React.PropsWithChildren<SinglePreferredOfferProps>
> = ({ riskObject, preferredOfferId }) => {
  const { t } = useTranslation();

  const { data: preferredOffer, isFetching } = useQuote(
    riskObject,
    preferredOfferId,
  );
  const offerState = getOfferState(preferredOffer);

  if (isFetching) return <Skeleton h="4" w="28" />;

  if (!preferredOffer)
    return (
      <HStack>
        {' '}
        <SingleSelectedOfferPrice price="-" interval="-" />
      </HStack>
    );

  return (
    <HStack>
      <SingleSelectedOfferPrice
        price={
          (getEuroValue(
            offerState,
            preferredOffer?.premium?.total_premium,
          ) as string) || '-'
        }
        interval={
          preferredOffer?.details?.payment_interval
            ? t(
                `spread.${preferredOffer.details.payment_interval.toUpperCase()}`,
              )
            : '-'
        }
      />
    </HStack>
  );
};

const StyledIconActionDropDown = styled(IconActionDropDown)`
  width: 3.2rem;
  height: 3.2rem;
`;

export default SingleSelectedOffer;
